import { Grid, Typography } from '@mui/material'
import moment from 'moment'
import React, { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useSyncJurnodesMutation } from '../../../graphql/mutations/m2Case/__generated__/syncJurnodesMutation.generated'
import { useJurnodesUsersQuery } from '../../../graphql/queries/__generated__/jurnodesUsersQuery.generated'
import { useCase, useNotification, useProfile } from '../../../utils/hooks'
import { Button, Select } from '../../atoms'
import Modal from '../Modal'

export interface JurnodesButtonProps {
  disabled?: boolean
}

const JurnodesButton = ({ disabled = false }: JurnodesButtonProps) => {
  const { t } = useTranslation(['common', 'jurnodes'])
  const { me } = useProfile()
  const { m2Case, refetch } = useCase()
  const { setNotification } = useNotification()
  const { data, error, loading } = useJurnodesUsersQuery()
  const [syncJurnodesMutation, { loading: syncLoading }] = useSyncJurnodesMutation()
  const [open, setOpen] = useState(false)
  const {
    formState: { isDirty }
  } = useFormContext()
  const [selectedUser, setSelectedUser] = useState(null)

  if (!m2Case?.project?.jurnodesSyncEnabled || !me?.jurnodesAllowed) {
    return null
  }
  return (
    <>
      <Grid container direction="column" spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Select
            items={
              data?.jurnodesUsers?.collection.map(user => ({ value: user.id, label: user.name })) ||
              []
            }
            value={selectedUser}
            onChange={value => {
              setSelectedUser(value)
            }}
            label={t('jurnodes:selectJurnodesUser')}
            disabled={
              loading || !!error || data?.jurnodesUsers?.collection?.length === 0 || isDirty
            }
            minWidth="100%"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button
            color="primary"
            onClick={() => setOpen(true)}
            fullWidth
            disabled={disabled || !selectedUser || isDirty}
          >
            {t('jurnodes:syncWithJurnodes')}
          </Button>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography
            variant="caption"
            sx={{ display: 'flex', width: '100%', whiteSpace: 'pre-line' }}
          >
            {m2Case.jurnodesLastSyncedAt
              ? t('jurnodes:lastSyncAt', {
                  date: m2Case.jurnodesLastSyncedAt
                    ? moment(m2Case.jurnodesLastSyncedAt).format('DD.MM.YYYY HH:mm')
                    : t('never')
                })
              : t('jurnodes:selectUserToSync')}
          </Typography>
        </Grid>
      </Grid>
      <Modal
        open={open}
        title={t('jurnodes:confirmSync')}
        content={t('jurnodes:confirmSyncMessage')}
        buttons={[
          {
            loading: syncLoading,
            color: 'primary',
            onClick: () => {
              syncJurnodesMutation({
                variables: {
                  caseId: m2Case.id,
                  userIdentifier: selectedUser
                },
                onCompleted: () => {
                  setNotification({
                    open: true,
                    variant: 'success',
                    title: t('common:success'),
                    message: t('jurnodes:syncSuccess')
                  })
                  setOpen(false)
                  refetch()
                },
                onError: error => {
                  setNotification({
                    open: true,
                    variant: 'error',
                    title: t('common:error'),
                    message: error?.message
                  })
                  setOpen(false)
                }
              })
            },
            label: t('common:yes')
          },
          {
            color: 'secondary',
            onClick: () => setOpen(false),
            label: t('common:no')
          }
        ]}
      />
    </>
  )
}

export default JurnodesButton
